import moment, { Moment } from 'moment';

import {
  MailMailboxFieldsFragment,
  StartMailHistoryLoadMutationVariables,
} from 'core/graphql';

/**
 * ТЗ 0122, «сначала свежее, история потом»: первичный импорт берёт только
 * последние дни, старую историю Admin догружает по кнопке. Чистая логика
 * строки «Почта с …» и формы «Загрузить историю» — без React.
 */

type HistoryFields = Pick<
  MailMailboxFieldsFragment,
  'historyFrom' | 'backfillStatus' | 'backfillProgress'
>;

export type MailHistoryState =
  /** Идёт догрузка: сколько писем уже добавлено. */
  | { kind: 'running'; added: number }
  /** Догрузка упала — показать предупреждение (и «повторить» для Admin). */
  | { kind: 'failed' }
  /** История загружена не вся: почта есть с этой даты. */
  | { kind: 'from'; from: Moment }
  /** Вся история на месте — ничего не показываем. */
  | null;

/**
 * Состояние истории одного ящика или сводка по нескольким: догрузка идёт
 * хоть в одном — «загружаем» (письма суммой); иначе упала хоть в одном —
 * предупреждение; иначе «почта с» самой поздней даты (с неё письма есть во
 * всех ящиках).
 */
export const mailHistoryState = (boxes: HistoryFields[]): MailHistoryState => {
  const running = boxes.filter((box) => box.backfillStatus === 'running');
  if (running.length > 0) {
    return {
      kind: 'running',
      added: running.reduce(
        (total, box) => total + (box.backfillProgress || 0),
        0,
      ),
    };
  }
  if (boxes.some((box) => box.backfillStatus === 'failed')) {
    return { kind: 'failed' };
  }
  let latest: Moment | null = null;
  boxes.forEach((box) => {
    if (!box.historyFrom) return;
    const value = moment(box.historyFrom);
    if (!value.isValid()) return;
    if (!latest || value.isAfter(latest)) latest = value;
  });
  return latest ? { kind: 'from', from: latest } : null;
};

/** Хоть в одном ящике идёт догрузка — список ящиков опрашиваем чаще. */
export const mailHistoryRunning = (boxes: HistoryFields[]): boolean =>
  boxes.some((box) => box.backfillStatus === 'running');

export type MailHistoryMode = 'days' | 'since' | 'all';

export interface IMailHistoryForm {
  mode: MailHistoryMode;
  days: number | null;
  since: Moment | null;
  /** `null` — все ящики, которые видит Admin. */
  mailboxId: string | null;
}

export const MAIL_HISTORY_DEFAULT_DAYS = 365;
export const MAIL_HISTORY_MAX_DAYS = 36500;

/**
 * Переменные `mailLoadHistory` из формы: ровно одно из days / since /
 * allHistory. `null` — форма не заполнена (нет числа дней или даты).
 */
export const mailHistoryVariables = (
  form: IMailHistoryForm,
): StartMailHistoryLoadMutationVariables | null => {
  const mailboxId = form.mailboxId || null;
  if (form.mode === 'all') return { mailboxId, allHistory: true };
  if (form.mode === 'days') {
    const days = Math.floor(Number(form.days));
    if (!Number.isFinite(days) || days < 1 || days > MAIL_HISTORY_MAX_DAYS) {
      return null;
    }
    return { mailboxId, days };
  }
  if (!form.since || !form.since.isValid()) return null;
  return { mailboxId, since: form.since.format('YYYY-MM-DD') };
};

/** Коды ошибок `mailLoadHistory` со своим объяснением в окне. */
const HISTORY_ERROR_CODES = ['INVALID', 'UNAVAILABLE', 'NO_ACCESS'];

export const mailHistoryErrorKey = (code?: string | null): string | null => {
  const value = (code || '').toUpperCase();
  return HISTORY_ERROR_CODES.includes(value)
    ? `mail.history.errors.${value}`
    : null;
};
